import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col } from "reactstrap";
import { toast } from "sonner";
import { useCreateFixedCostMutation, useUpdateFixedCostMutation } from "store/features/fixedCosts/fixedCostsManagementApi";

const FixedCostModal = ({
  isOpen,
  toggle,
  onSubmit,
  fixedCost
}) => {
  const [createFixedCost, { isLoading: creating }] = useCreateFixedCostMutation();
  const [updateFixedCost, { isLoading: updating }] = useUpdateFixedCostMutation();

  const [formData, setFormData] = useState({
    title: "",
    amount: "",
    date: ""
  });

  useEffect(() => {
    if (fixedCost) {
      // Editing existing fixed cost
      setFormData({
        title: fixedCost.title || "",
        amount: fixedCost.amount || "",
        date: fixedCost.date || ""
      });
    } else {
      // Adding new fixed cost
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        title: "",
        amount: "",
        date: today
      });
    }
  }, [fixedCost, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error("Please enter valid amount");
      return;
    }

    try {
      const fixedCostData = {
        title: formData.title,
        amount: parseFloat(formData.amount),
        date: formData.date || null
      };

      if (fixedCost) {
        // Update existing fixed cost
        console.log("fixedCostData", fixedCostData);
        await updateFixedCost({
          id: fixedCost.id,
          ...fixedCostData
        }).unwrap();
      } else {
        // Create new fixed cost
        await createFixedCost(fixedCostData).unwrap();
      }

      toast.success(`Fixed cost ${fixedCost ? 'updated' : 'created'} successfully!`);
      onSubmit();
    } catch (error) {
      toast.error(`Failed to ${fixedCost ? 'update' : 'create'} fixed cost`);
      console.error('Fixed cost operation error:', error);
    }
  };

  const handleModalClose = () => {
    setFormData({
      title: "",
      amount: "",
      date: ""
    });
    toggle();
  };

  const isLoading = creating || updating;

  return (
    <Modal isOpen={isOpen} toggle={handleModalClose}>
      <ModalHeader toggle={handleModalClose}>
        {fixedCost ? "Edit Fixed Cost" : "Create Fixed Cost"}
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <Row>
            <Col lg={12}>
              <div className="mb-3">
                <label className="form-label">Title *</label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter cost title"
                  required
                />
              </div>
            </Col>
            <Col lg={12}>
              <div className="mb-3">
                <label className="form-label">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="Enter amount"
                  required
                />
              </div>
            </Col>
            <Col lg={12}>
              <div className="mb-3">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="light" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={isLoading}>
            {isLoading
              ? (fixedCost ? "Updating..." : "Creating...")
              : (fixedCost ? "Update Fixed Cost" : "Create Fixed Cost")
            }
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default FixedCostModal;